import type { ITemplateRepository } from "@/application/ports/repositories";
import type {
  CatalogResponse,
  DuplicateTemplateRequest,
  PaginatedTemplates,
  TemplateCreateRequest,
  TemplateDetail,
  TemplateFilterState,
  TemplateImagePreviewResponse,
  TemplateImageUploadRequest,
  TemplateImageUploadResponse,
  TemplateUpdateRequest,
} from "@/domain/template";
import * as sdk from "../generated/sdk.gen";
import { unwrap, unwrapBlob } from "../unwrap";

export class TemplateRepository implements ITemplateRepository {
  async getTemplates(
    filters?: TemplateFilterState,
    signal?: AbortSignal,
  ): Promise<PaginatedTemplates> {
    const { items, ...rest } = await unwrap(sdk.listTemplates({ query: filters, signal }));
    return { templates: items, ...rest };
  }

  async getTemplate(uuid: string): Promise<TemplateDetail> {
    return unwrap(sdk.getTemplate({ path: { template_uuid: uuid } }));
  }

  async createTemplate(data: TemplateCreateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.createTemplate({ body: data }));
  }

  async updateTemplate(uuid: string, data: TemplateUpdateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.updateTemplate({ path: { template_uuid: uuid }, body: data }));
  }

  async duplicateTemplate(uuid: string, data: DuplicateTemplateRequest): Promise<TemplateDetail> {
    return unwrap(sdk.duplicateTemplate({ path: { template_uuid: uuid }, body: data }));
  }

  async deleteTemplate(uuid: string): Promise<void> {
    await unwrap(sdk.deleteTemplate({ path: { template_uuid: uuid } }));
  }

  async getCatalog(): Promise<CatalogResponse> {
    return unwrap(sdk.getCatalog());
  }

  async previewTemplate(uuid: string): Promise<Blob> {
    return unwrapBlob(sdk.previewTemplate({ path: { template_uuid: uuid } }));
  }

  async uploadTemplateImage(
    uuid: string,
    data: TemplateImageUploadRequest,
  ): Promise<TemplateImageUploadResponse> {
    const response = await unwrap(
      sdk.uploadTemplateImage({
        path: { template_uuid: uuid },
        body: { data_uri: data.dataUri },
      }),
    );
    return { src: response.src, signedUrl: response.signed_url };
  }

  async getTemplateImagePreview(uuid: string, src: string): Promise<TemplateImagePreviewResponse> {
    const response = await unwrap(
      sdk.getTemplateImagePreview({
        path: { template_uuid: uuid },
        body: { src },
      }),
    );
    return { signedUrl: response.signed_url };
  }
}
